import { useMemo, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router';
import { BellOff, Bell, Check, Compass, Copy, LogOut, Plus, Settings, Shield, UserPlus } from 'lucide-react';
import { Permission } from '@spawnwire/shared';
import { LogoMark } from '../../components/Logo';
import { Badge, clsx, ContextMenu, Tooltip, type MenuEntry } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt, markSpaceRead } from '../../lib/data';
import { copyText, useAppRoute } from '../../lib/hooks';
import { lastChannel } from '../../lib/local';
import { can, spacePermissions } from '../../lib/perms';
import { isMuted, isUnread, mentionCount, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { SpaceIcon } from './SpaceIcon';

function orderedSpaceIds(ids: string[], order: string[]): string[] {
  const known = order.filter((id) => ids.includes(id));
  const rest = ids.filter((id) => !known.includes(id)).sort((a, b) => a.localeCompare(b));
  return [...known, ...rest];
}

function RailButton({ active, unread, mentions, label, children, onClick }: { active: boolean; unread: boolean; mentions: number; label: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <div className="group relative flex w-full justify-center">
      <span
        className={clsx(
          'absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r-full bg-fg transition-all',
          active ? 'h-10' : unread ? 'h-2' : 'h-0 group-hover:h-5',
        )}
        aria-hidden
      />
      <Tooltip label={label} side="right">
        <button type="button" onClick={onClick} aria-label={label} aria-current={active ? 'page' : undefined} className="relative">
          {children}
          {mentions > 0 && <Badge count={mentions} className="absolute -bottom-0.5 -right-1 ring-[3px] ring-rail" />}
        </button>
      </Tooltip>
    </div>
  );
}

export function SpaceRail() {
  const navigate = useNavigate();
  const { spaceId: activeSpace, home, admin, discover } = useAppRoute();
  const spaces = useStore((s) => s.spaces);
  const channels = useStore((s) => s.channels);
  const readStates = useStore((s) => s.readStates);
  const notificationSettings = useStore((s) => s.notificationSettings);
  const relationships = useStore((s) => s.relationships);
  const me = useStore((s) => s.me);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const spaceOrder = me?.settings.spaceOrder;
  const ids = useMemo(() => orderedSpaceIds(Object.keys(spaces), spaceOrder ?? []), [spaces, spaceOrder]);

  const status = useMemo(() => {
    const state = { readStates, notificationSettings };
    const out: Record<string, { unread: boolean; mentions: number }> = {};
    let dmMentions = 0;
    for (const c of Object.values(channels)) {
      if (c.type === 'category' || c.type === 'voice' || c.type === 'stage') continue;
      const key = c.spaceId ?? '@me';
      const entry = (out[key] ??= { unread: false, mentions: 0 });
      const mentions = mentionCount(state, c.id);
      entry.mentions += mentions;
      if (!c.spaceId) dmMentions += mentions;
      if (isUnread(state, c) && !isMuted(state, c.id) && !isMuted(state, c.spaceId)) entry.unread = true;
    }
    const pending = Object.values(relationships).filter((r) => r.type === 'incoming').length;
    return { out, home: dmMentions + pending };
  }, [channels, readStates, notificationSettings, relationships]);

  function openSpace(id: string) {
    const last = lastChannel.get(id);
    const state = useStore.getState();
    const target = last && state.channels[last] ? last : null;
    navigate(target ? `/app/${id}/${target}` : `/app/${id}`);
  }

  function menuFor(spaceId: string): MenuEntry[] {
    const state = useStore.getState();
    const space = state.spaces[spaceId]!;
    const perms = spacePermissions(state, spaceId);
    const muted = isMuted(state, spaceId);
    const firstText = Object.values(state.channels).find((c) => c.spaceId === spaceId && c.type === 'text');
    return [
      { label: 'Mark as read', icon: Check, onSelect: () => markSpaceRead(spaceId) },
      { label: 'Invite people', icon: UserPlus, hidden: !can(perms, Permission.CREATE_INVITE) || !firstText, onSelect: () => firstText && ui.open({ kind: 'invite', spaceId, channelId: firstText.id }), separatorBefore: true },
      {
        label: muted ? 'Unmute space' : 'Mute space',
        icon: muted ? Bell : BellOff,
        onSelect: () =>
          void attempt(() =>
            http.put(`/users/@me/notification-settings/${spaceId}`, { mutedUntil: muted ? null : new Date(Date.now() + 365 * 86400_000).toISOString() }),
          ),
      },
      { label: 'Space settings', icon: Settings, onSelect: () => ui.open({ kind: 'spaceSettings', spaceId }) },
      { label: 'Copy space ID', icon: Copy, onSelect: () => void copyText(spaceId) },
      {
        label: 'Leave space',
        icon: LogOut,
        danger: true,
        separatorBefore: true,
        hidden: space.ownerId === state.me?.id,
        onSelect: () =>
          ui.confirm({
            title: `Leave ${space.name}?`,
            body: 'You will need a new invite to rejoin.',
            confirmLabel: 'Leave space',
            danger: true,
            onConfirm: async () => {
              await http.del(`/spaces/${spaceId}/members/@me`);
              navigate('/app/@me');
            },
          }),
      },
    ];
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    if (dragId === null || dropIndex === null) return;
    const next = ids.filter((id) => id !== dragId);
    const from = ids.indexOf(dragId);
    next.splice(dropIndex > from ? dropIndex - 1 : dropIndex, 0, dragId);
    setDragId(null);
    setDropIndex(null);
    void attempt(() => http.patch('/users/@me/settings', { spaceOrder: next }));
  }

  return (
    <nav aria-label="Spaces" className="scroll-thin flex w-[72px] shrink-0 flex-col items-center gap-2 overflow-y-auto bg-rail pt-1 pb-3">
      <RailButton active={home} unread={false} mentions={status.home} label="Direct messages" onClick={() => navigate('/app/@me')}>
        <span className={clsx('flex size-12 items-center justify-center transition-[border-radius]', home ? 'rounded-[30%]' : 'rounded-[50%] hover:rounded-[30%]')}>
          <LogoMark size={48} className={clsx('transition-[border-radius]')} />
        </span>
      </RailButton>
      <div className="h-0.5 w-8 rounded bg-line" aria-hidden />

      <ul className="flex w-full flex-col items-center gap-2" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
        {ids.map((id, index) => {
          const space = spaces[id]!;
          const st = status.out[id] ?? { unread: false, mentions: 0 };
          return (
            <li
              key={id}
              className="relative flex w-full justify-center"
              draggable
              onDragStart={(e) => {
                setDragId(id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', id);
              }}
              onDragEnd={() => {
                setDragId(null);
                setDropIndex(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                setDropIndex(e.clientY < rect.top + rect.height / 2 ? index : index + 1);
              }}
            >
              {dragId && dropIndex === index && <span className="absolute -top-1.5 h-1 w-10 rounded bg-accent" aria-hidden />}
              {dragId && dropIndex === index + 1 && index === ids.length - 1 && <span className="absolute -bottom-1.5 h-1 w-10 rounded bg-accent" aria-hidden />}
              <ContextMenu entries={menuFor(id)}>
                <div className="w-full">
                  <RailButton active={activeSpace === id} unread={st.unread} mentions={st.mentions} label={space.name} onClick={() => openSpace(id)}>
                    <SpaceIcon name={space.name} iconUrl={space.iconUrl} active={activeSpace === id} className={clsx(dragId === id && 'opacity-40', 'hover:rounded-[30%]')} />
                  </RailButton>
                </div>
              </ContextMenu>
            </li>
          );
        })}
      </ul>

      <Tooltip label="Create a space" side="right">
        <button
          type="button"
          aria-label="Create a space"
          onClick={() => ui.open({ kind: 'createSpace' })}
          className="flex size-12 items-center justify-center rounded-[50%] bg-sidebar text-accent transition-all hover:rounded-[30%] hover:bg-accent hover:text-accent-fg"
        >
          <Plus className="size-6" />
        </button>
      </Tooltip>
      <RailButton active={discover} unread={false} mentions={0} label="Discover communities" onClick={() => navigate('/app/discover')}>
        <span
          className={clsx(
            'flex size-12 items-center justify-center transition-all',
            discover ? 'rounded-[30%] bg-accent text-accent-fg' : 'rounded-[50%] bg-sidebar text-accent hover:rounded-[30%] hover:bg-accent hover:text-accent-fg',
          )}
        >
          <Compass className="size-6" aria-hidden />
        </span>
      </RailButton>
      {me?.staffRole && (
        <RailButton active={admin} unread={false} mentions={0} label="Admin panel" onClick={() => navigate('/app/admin')}>
          <span
            className={clsx(
              'flex size-12 items-center justify-center transition-all',
              admin ? 'rounded-[30%] bg-danger text-white' : 'rounded-[50%] bg-sidebar text-danger hover:rounded-[30%] hover:bg-danger hover:text-white',
            )}
          >
            <Shield className="size-6" aria-hidden />
          </span>
        </RailButton>
      )}
    </nav>
  );
}
